import { AgvanceAPI, SkyAPI, UserAPI } from "@ai360/core";

import { AgvanceUtils } from "./agvance-utils";

export type LinkedProductGuids = ReadonlySet<string> | readonly string[] | null;

export type LinkedProductScopeKey = { customerGuid: string } | { orgLevelGuid: string };

export interface ILinkedProductScope {
    /** False when the flag is off, Agvance is not connected, or no scope could be resolved. */
    isEnabled: boolean;
    scopeKey: LinkedProductScopeKey;
}

export interface ILinkedProductScopeInput {
    companyGuid?: string | null;
    /** Takes precedence over the org level. */
    customerGuid?: string | null;
    lastUsedLocationGuid?: string | null;
    /** Overrides the logged-in user's own org level. This is used for admin level workflows*/
    orgLevelGuid?: string | null;
    personalityId: number;
    user: Pick<UserAPI.IUser, "linkedProductOnly"> | null;
}

export const isLinkedProductOnlyEnabled = (
    user: Pick<UserAPI.IUser, "linkedProductOnly"> | null,
    personalityId: number
): boolean => Boolean(user?.linkedProductOnly && AgvanceUtils.isAgvanceConnected(personalityId));

export const resolveLinkedProductScope = ({
    companyGuid = null,
    customerGuid = null,
    lastUsedLocationGuid = null,
    orgLevelGuid = null,
    personalityId,
    user,
}: ILinkedProductScopeInput): ILinkedProductScope => {
    const resolvedOrgLevelGuid = orgLevelGuid ?? (lastUsedLocationGuid || companyGuid);
    return {
        isEnabled:
            isLinkedProductOnlyEnabled(user, personalityId) &&
            Boolean(customerGuid || resolvedOrgLevelGuid),
        scopeKey: customerGuid ? { customerGuid } : { orgLevelGuid: resolvedOrgLevelGuid },
    };
};

const hasGuid = (linkedProductGuids: ReadonlySet<string> | readonly string[], guid: string) =>
    linkedProductGuids instanceof Set
        ? linkedProductGuids.has(guid)
        : (linkedProductGuids as readonly string[]).includes(guid);

/** Null guids mean unrestricted. */
export const isProductLinked = (
    productGuid: string,
    linkedProductGuids: LinkedProductGuids
): boolean => linkedProductGuids == null || hasGuid(linkedProductGuids, productGuid);

export const isProductUnlinked = (
    productGuid: string,
    linkedProductGuids: LinkedProductGuids
): boolean => Boolean(productGuid) && !isProductLinked(productGuid, linkedProductGuids);

export const toAgvanceLocations = (
    locations: SkyAPI.ILocationResponse[] | null
): AgvanceAPI.IProductListMultiLocation[] =>
    (locations ?? [])
        .filter((location) => Boolean(location.agvanceLocationId))
        .map((location) => ({
            agvanceLocationId: location.agvanceLocationId,
            ...(location.locationGuid && { locationGuid: location.locationGuid }),
        }));

export const toLinkedProductGuids = (products: AgvanceAPI.IProduct[] | null): string[] =>
    (products ?? []).map((product) => product.mappingGuid).filter(Boolean);

/**
 * Narrows a list to the Agvance-linked products. Null set means unrestricted. Falsy guids are kept
 * (the equation filter's empty option, which is what makes a filter optional), as are retainGuids.
 */
export const filterLinkedProducts = <T>(
    items: T[],
    linkedProductGuids: LinkedProductGuids,
    getGuid: (item: T) => string,
    retainGuids?: Iterable<string>
): T[] => {
    if (linkedProductGuids == null) {
        return items ?? [];
    }
    const retained = new Set(retainGuids ?? []);
    return (items ?? []).filter((item) => {
        const guid = getGuid(item);
        return !guid || isProductLinked(guid, linkedProductGuids) || retained.has(guid);
    });
};
